import { when } from '../bailey/format';
import { describeOffer, dayLabel } from './transcript';

/**
 * Messages Bailey took, made into what Needs you shows: one row per person,
 * the most pressing first, and the next step Bailey has ready for each.
 *
 * Pure: no requests and no React. The conversations the messages came from
 * are passed in as they load; until a person's is in, their row falls back to
 * calling or replying.
 */

const DAY = 86400000;
// Older than this, a message folds into Earlier on the full list.
export const EARLIER_DAYS = 7;

const time = (v) => { const t = new Date(v || 0).getTime(); return Number.isFinite(t) ? t : 0; };
const norm = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
export const firstName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/* ------------------------------------------------------------- contact */
const EMAIL = /[^\s@<>()[\],;:"'/]+@[^\s@<>()[\],;:"'/]+\.[a-z]{2,}/i;
const PHONE = /\+?\d[\d\s().-]{5,}\d/;

/**
 * The number and the email in what the client gave, which Bailey often saves
 * as one ("07700 900284 / sarah@example.com").
 */
export function contactOf(raw) {
    const s = String(raw || '');
    const e = EMAIL.exec(s);
    const email = e ? e[0] : '';
    const p = PHONE.exec(e ? s.replace(e[0], ' ') : s);
    const digits = p ? p[0].replace(/\D/g, '') : '';
    return { phone: digits.length >= 7 && digits.length <= 15 ? p[0].trim() : '', email };
}

// The last ten digits, so 07700 900284 and +44 7700 900284 are one number.
const phoneKey = (phone) => { const d = String(phone || '').replace(/\D/g, ''); return d.length >= 7 ? `p:${d.slice(-10)}` : ''; };

// "Sarah" and "Sarah Jennings" can be one person; "Sarah" and "Christine" can't.
const fits = (a, b) => !a || !b || a === b || a.startsWith(`${b} `) || b.startsWith(`${a} `);
const namesFit = (as, bs) => [...as].every((a) => [...bs].every((b) => fits(a, b)));

/* -------------------------------------------------------- what they want */
const UNHAPPY = /\b(?:unhappy|not happy|complain(?:t|ts|ed|ing)?|frustrated|disappointed|upset|angry|furious|refund|put (?:it |this |that )?right|gone wrong|went wrong|ruined)\b/i;
const QUESTION = /\?|\b(?:asks?|asking|asked|like to know|wants? to know|wondering|whether|enquir(?:y|ies|ing)|question)\b/i;

export const isUnhappy = (text) => UNHAPPY.test(String(text || ''));
export const isQuestion = (text) => QUESTION.test(String(text || ''));

/* ---------------------------------------------------------------- groups */
/**
 * One group per person, newest message first. Messages from the same
 * conversation are always one person; a shared number or email joins them
 * only when the names agree, so a family landline doesn't merge two people;
 * and the same full name joins them even from different numbers.
 */
export function groupMessages(messages) {
    const list = (messages || []).filter((m) => m && m.id);
    const contacts = list.map((m) => contactOf(m.contact));
    const parent = list.map((_, i) => i);
    const names = list.map((m) => new Set(m.name ? [norm(m.name)] : []));
    const find = (i) => { let x = i; while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
    const join = (a, b, check) => {
        const ra = find(a); const rb = find(b);
        if (ra === rb) return true;
        if (check && !namesFit(names[ra], names[rb])) return false;
        parent[rb] = ra;
        names[rb].forEach((n) => names[ra].add(n));
        return true;
    };
    const byKey = (keyOf, check) => {
        const seen = {};
        list.forEach((m, i) => {
            const k = keyOf(m, i);
            if (!k) return;
            const roots = seen[k] || (seen[k] = []);
            if (!roots.some((r) => join(r, i, check))) roots.push(i);
        });
    };
    byKey((m) => m.sessionId && `s:${m.sessionId}`, false);
    byKey((m, i) => contacts[i].email && `e:${contacts[i].email.toLowerCase()}`, true);
    byKey((m, i) => phoneKey(contacts[i].phone), true);
    byKey((m) => { const k = norm(m.name); return k.includes(' ') ? `n:${k}` : ''; }, true);

    const buckets = new Map();
    list.forEach((m, i) => {
        const r = find(i);
        if (!buckets.has(r)) buckets.set(r, []);
        buckets.get(r).push(i);
    });
    return [...buckets.values()].map((idx) => {
        const rows = idx.map((i) => ({ m: list[i], c: contacts[i] })).sort((a, b) => time(b.m.at) - time(a.m.at));
        const msgs = rows.map((r) => r.m);
        const named = msgs.map((m) => String(m.name || '').trim()).filter(Boolean).sort((a, b) => b.length - a.length);
        // Every number and email they gave, newest first; the newest is the one to use.
        const phones = distinct(rows.map((r) => r.c.phone), (p) => phoneKey(p));
        const emails = distinct(rows.map((r) => r.c.email), (e) => e.toLowerCase());
        const oldest = msgs[msgs.length - 1];
        return {
            key: `g-${oldest.id}`,
            ids: msgs.map((m) => m.id),
            messages: msgs,
            latest: msgs[0],
            count: msgs.length,
            name: named[0] || '',
            phone: phones[0] || '',
            email: emails[0] || '',
            phones,
            emails,
            sessionIds: [...new Set(msgs.map((m) => m.sessionId).filter(Boolean))],
            at: msgs[0].at,
            since: oldest.at,
            unhappy: msgs.some((m) => isUnhappy(m.text)),
            question: msgs.some((m) => isQuestion(m.text))
        };
    });
}

function distinct(values, keyOf) {
    const seen = new Set();
    return values.filter((v) => {
        if (!v || seen.has(keyOf(v))) return false;
        seen.add(keyOf(v));
        return true;
    });
}

export const isEarlier = (g, now = new Date()) => time(g.at) < now.getTime() - EARLIER_DAYS * DAY;

/**
 * Unhappy clients first, then whoever has waited longest. Anyone whose
 * latest message is over a week old goes last, newest first.
 */
export function rankGroups(groups, now = new Date()) {
    const tier = (g) => (isEarlier(g, now) ? 2 : g.unhappy ? 0 : 1);
    return (groups || []).slice().sort((a, b) => {
        const t = tier(a) - tier(b);
        if (t) return t;
        if (tier(a) === 2) return time(b.at) - time(a.at);
        return time(a.since) - time(b.since);
    });
}

/** The people to show, ranked, from the open messages. */
export const needsFrom = (messages, now = new Date()) => rankGroups(groupMessages(messages), now);

/** "07700 900284 · sarah@example.com", with every number and email they gave. */
export const reachOf = (g) => [...(g.phones || []), ...(g.emails || [])].join(' · ');

/** "49 messages from 27 people, waiting since 7:35am". */
export function needsLine(groups, now = new Date()) {
    const all = groups || [];
    const total = all.reduce((n, g) => n + g.count, 0);
    if (!total) return '';
    const people = all.length;
    let line = `${total} ${total === 1 ? 'message' : 'messages'}`;
    if (people !== total) line += ` from ${people} ${people === 1 ? 'person' : 'people'}`;
    const recent = all.filter((g) => !isEarlier(g, now));
    const oldest = (recent.length ? recent : all).reduce((t, g) => Math.min(t, time(g.since)), Infinity);
    const w = Number.isFinite(oldest) ? when(oldest, now) : '';
    if (w && w !== 'Just now') line += `, waiting since ${w === 'Yesterday' ? 'yesterday' : w}`;
    return line;
}

/* ------------------------------------------------------------ next step */
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * The booking Bailey worked out with this person, newest conversation
 * first, when it is still ahead and nobody has booked it.
 */
export function bookingFor(g, conversations = {}, now = new Date()) {
    const today = ymd(now);
    for (let i = 0; i < g.sessionIds.length; i += 1) {
        const c = conversations[g.sessionIds[i]];
        if (c && c.outcome === 'booked') return null;
        const b = c && c.booking;
        if (b && b.date >= today) return b;
    }
    return null;
}

/** "9am", "9:30am". */
export function shortTime(hhmm) {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
    if (!m) return '';
    const h = Number(m[1]);
    return `${(h % 12) || 12}${m[2] === '00' ? '' : `:${m[2]}`}${h < 12 ? 'am' : 'pm'}`;
}

/** "Book Sat 3 Oct, 9am". */
export const bookLabel = (b) => `Book ${dayLabel(b.date)}, ${shortTime(b.slot)}`;

/** "Ladies Cut and Finish and Full Head Colour with Phoebe, Sat 3 Oct at 9:00am". */
export const describeBooking = (b) => describeOffer({ services: b.services, stylist: b.stylist, date: b.date, slot: b.slot }, { withPrice: false });

/**
 * What Bailey has ready for this person, as one step:
 *   book    the booking from their conversation, into the diary
 *   reply   a reply drafted for them to send by email or text
 *   answer  no way to reach them: teach Bailey the answer instead
 *   call    ring them
 *   email   write to them
 *   look    nothing to act on: read the conversation
 * Unhappy clients get a person, so a call when there's a number.
 */
export function nextStep(g, conversations = {}, now = new Date()) {
    if (g.unhappy) {
        if (g.phone) return { kind: 'call' };
        if (g.email) return { kind: 'reply' };
        return { kind: 'look' };
    }
    // Only someone who can be told about it: a booking for nobody can't be confirmed.
    const booking = (g.phone || g.email) && bookingFor(g, conversations, now);
    if (booking) return { kind: 'book', booking };
    if (g.question) return g.phone || g.email ? { kind: 'reply' } : { kind: 'answer' };
    if (g.phone) return { kind: 'call' };
    if (g.email) return { kind: 'email' };
    return { kind: 'look' };
}

const STEP_LABELS = { reply: 'Reply', answer: 'Add the answer', call: 'Call', email: 'Email', look: 'Read it' };
export const stepLabel = (step) => (step.kind === 'book' ? bookLabel(step.booking) : STEP_LABELS[step.kind] || 'Read it');

/** The row's title: who, and what they're waiting for. */
export function groupTitle(g, step) {
    const who = g.name || 'A client';
    if (g.unhappy) return `${who} is unhappy`;
    if (step && step.kind === 'book') return `${who} wants to book`;
    if (g.question) return g.name ? `${who} asked a question` : 'A client asked a question';
    if (g.phone) return `${who} wants a call back`;
    if (g.email) return `${who} would like a reply`;
    return `${who} left a message`;
}

/* --------------------------------------------------- filters and summary */
/**
 * What each person is waiting for, as their card's title says it: unhappy
 * first, then a booking to make, a question, a call back, and the rest (an
 * email with nothing asked, or no way to reach them). One each, so the
 * filter counts add up to everyone waiting.
 */
export const KINDS = [
    { id: 'unhappy', label: 'Unhappy', none: 'No unhappy clients are waiting.' },
    { id: 'book', label: 'Want to book', none: 'No booking requests are waiting.' },
    { id: 'question', label: 'Questions', none: 'No questions are waiting.' },
    { id: 'call', label: 'Call backs', none: 'No call backs are waiting.' },
    { id: 'other', label: 'Other', none: 'Nothing else is waiting.' }
];

export function kindOf(g, step) {
    if (g.unhappy) return 'unhappy';
    if (step && step.kind === 'book') return 'book';
    if (g.question) return 'question';
    if (g.phone) return 'call';
    return 'other';
}

/** Each person's next step and kind (by their key), and how many there are of each kind. */
export function sortKinds(groups, conversations = {}, now = new Date()) {
    const steps = {};
    const by = {};
    const counts = KINDS.reduce((o, k) => ({ ...o, [k.id]: 0 }), {});
    (groups || []).forEach((g) => {
        steps[g.key] = nextStep(g, conversations, now);
        by[g.key] = kindOf(g, steps[g.key]);
        counts[by[g.key]] += 1;
    });
    return { steps, by, counts };
}

/**
 * Only the people of one kind ('all' is everyone). Bookings go soonest
 * first, since it's the date that decides which to do first.
 */
export function ofKind(groups, kinds, kind) {
    if (kind === 'all') return groups;
    const list = groups.filter((g) => kinds.by[g.key] === kind);
    if (kind !== 'book') return list;
    const when = (g) => { const b = kinds.steps[g.key].booking; return `${b.date} ${b.slot}`; };
    return list.slice().sort((a, b) => (when(a) < when(b) ? -1 : when(a) > when(b) ? 1 : 0));
}

// More people than this waiting, and Bailey says where to start.
export const BRIEF_OVER = 5;

const joined = (parts) => (parts.length < 2 ? parts[0] || '' : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`);

// The counts of the rest, the way you'd say them.
const COUNT_WORDS = {
    book: (n) => (n === 1 ? '1 wants to book' : `${n} want to book`),
    question: (n) => (n === 1 ? '1 asked a question' : `${n} asked questions`),
    call: (n) => (n === 1 ? '1 wants a call back' : `${n} want a call back`),
    other: (n) => (n === 1 ? '1 left a message' : `${n} left messages`)
};

/**
 * Bailey's one or two lines at the top of a long list: who to start with
 * (the unhappy), then how the rest break down. Built from what's already
 * known about each person, so it never says more than the cards do.
 * Nothing at all for a short list. Names are used as given, never a he or a she.
 */
export function briefOf(groups, kinds) {
    const all = groups || [];
    if (all.length <= BRIEF_OVER) return '';
    const unhappy = all.filter((g) => kinds.by[g.key] === 'unhappy');
    let start = '';
    if (unhappy.length === 1) start = unhappy[0].name ? `I’d start with ${unhappy[0].name}, who is unhappy.` : 'I’d start with the client who is unhappy.';
    else if (unhappy.length === 2 && unhappy.every((g) => g.name)) start = `I’d start with ${unhappy[0].name} and ${unhappy[1].name}, who are unhappy.`;
    else if (unhappy.length) start = `I’d start with the ${unhappy.length} unhappy clients.`;
    const rest = ['book', 'question', 'call', 'other'].filter((k) => kinds.counts[k]).map((k) => COUNT_WORDS[k](kinds.counts[k]));
    if (!rest.length) return start;
    return `${start ? `${start} Of the rest, ` : ''}${joined(rest)}.`;
}

/* ---------------------------------------------------------------- reply */
const STOP = new Set(['about', 'after', 'also', 'any', 'are', 'asks', 'asked', 'asking', 'back', 'before', 'being', 'book', 'booking', 'call', 'can', 'client', 'could', 'does', 'from', 'have', 'her', 'his', 'how', 'into', 'just', 'know', 'like', 'please', 'salon', 'she', 'that', 'the', 'their', 'them', 'there', 'they', 'this', 'what', 'when', 'where', 'whether', 'which', 'will', 'with', 'would', 'you', 'your']);
const words = (s) => norm(s).replace(/[^a-z0-9\s]/g, ' ').split(' ').filter((w) => w.length >= 4 && !STOP.has(w));
const near = (a, b) => a === b || (a.length >= 4 && b.length >= 4 && (a.startsWith(b) || b.startsWith(a)));

/**
 * The saved answer that fits what they asked, if any. Every word of the
 * answer's own question has to be in the message ("park" finds "parking"),
 * or most of them when there are several, so "Do you take cash?" never
 * answers a question about deposits.
 */
export function matchAnswer(text, answers) {
    const said = words(text);
    let best = null;
    (answers || []).forEach((a) => {
        const asked = words(a.question);
        if (!asked.length || !String(a.answer || '').trim()) return;
        const hit = asked.filter((w) => said.some((s) => near(s, w))).length;
        const score = hit / asked.length;
        const ok = hit === asked.length || (hit >= 2 && score >= 0.6);
        if (ok && (!best || score > best.score)) best = { score, answer: a };
    });
    return best ? best.answer : null;
}

// "Client asks", "Margaret would like to know", "Sarah is asking": who asked, to drop.
const ASKED = /^(?:[Tt]he )?(?:[Cc]lient|[A-Z][\w'-]*(?: [A-Z][\w'-]*){0,2})(?: is)? (?:asks?|asking|asked|wants to know|would like to know|wondering|wonders)\s+(?:about\s+)?/;

/** The question for "Add the answer", from Bailey's note ("Client asks which colour…"). */
export function questionFrom(text) {
    const first = String(text || '').trim().split(/(?:[.!?])\s/)[0].replace(/[.!]+$/, '');
    const q = first.replace(ASKED, '');
    return q ? q.charAt(0).toUpperCase() + q.slice(1) : '';
}

/**
 * A reply in the salon's voice: hello, the saved answer when there is one,
 * and a sign-off. Without an answer the middle is left for the owner, and
 * `gap` says where to put the cursor.
 */
export function replyDraft({ group, answers = [], salonName = '' }) {
    const first = firstName(group && group.name);
    const asked = ((group && group.messages) || []).map((m) => m.text).join(' ');
    const known = matchAnswer(asked, answers);
    const top = `${first ? `Hi ${first}` : 'Hi'},\n\nThanks for your message. `;
    const middle = known ? String(known.answer).trim() : '';
    const end = `\n\n${salonName ? `Thanks,\n${salonName}` : 'Thanks'}`;
    return { text: `${top}${middle}${end}`, gap: top.length + middle.length, known };
}
